// Auth.js
import React, { useState, useEffect } from "react";
import { Container, Button, Box, Typography, inputAdornmentClasses } from "@mui/material";
import { useNavigate } from "react-router-dom";
import OTPInput from "react-otp-input";
import { isWeb } from "../../utils/platform";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { analytics, handlePhoneNumberError, initializeRecaptcha } from "../../helper/helper";
import Heading from "../../components/heading";
import { useDispatch } from "react-redux";
import { backend, markPinNotExist } from "../../reducers/userReducer";
import axios from "axios";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { auth, signInWithPhoneNumber } from "../../service/firebase";

const ResetPin = ({
  confirmationResult,
  setConfirmationResult,
  setOtpSent,
  error,
  phone,
  isAnonymous,
  email,
  verificationMethod,
  cooldown,
  setCooldown,
  setError,
  setLoader,
  isResendDisabled,
  setIsResendDisabled,
}) => {
  const [otp, setOtp] = useState("");
  const [cooldownTime, setCooldownTime] = useState(60);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    let interval;
    if (cooldown) {
      interval = setInterval(() => {
        setCooldownTime((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(interval);
            setCooldown(false);
            setIsResendDisabled(false);
            return 60;
          }
          return prevTime - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [cooldown, setCooldown, setIsResendDisabled]);

  const handleResendOtp = async () => {
    if (cooldown) return;
    
    setLoader(true);
    try {
      if (verificationMethod === "email") {
        analytics.track("Email OTP Resend Requested", { email });
        const response = await fetch(`${backend}/auth/pin/reset/send-otp`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        });

        if (!response.ok) {
          throw new Error("Failed to resend email OTP");
        }

        setIsResendDisabled(true);
        setCooldown(true);
        setCooldownTime(60);
        analytics.track("Email OTP Resent", { email });
      } else {
        analytics.track("OTP Resend Requested", { phone });
        if (isWeb()) {
          const appVerifier = window.recaptchaVerifier;
          const result = await signInWithPhoneNumber(auth, phone, appVerifier);
          setConfirmationResult(result);
        } else {
          await FirebaseAuthentication.signInWithPhoneNumber({
            phoneNumber: phone,
          });
        }
        setIsResendDisabled(true);
        setCooldown(true);
        setCooldownTime(60);
        analytics.track("OTP Resent", { phone });
      }
    } catch (error) {
      if (verificationMethod === "email") {
        setError("Failed to resend email OTP. Please try again.");
      } else {
        handlePhoneNumberError(error, setError, setLoader, setCooldown, phone);
      }
    } finally {
      setLoader(false);
    }
  };

  const handleVerifyOtp = async () => {
    setLoader(true);
    analytics.track("OTP Verification Attempt", { 
      [verificationMethod]: verificationMethod === "phone" ? phone : email,
      otp 
    });

    try {
      if (verificationMethod === "email") {
        // Verify email OTP
        const response = await fetch(`${backend}/auth/pin/reset/verify-otp`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            otp,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to verify email OTP');
        }

        dispatch(markPinNotExist());
        navigate("/pin/set");
        analytics.track("OTP Verification reset pin Success", { email });
      } else {
        // Verify phone OTP
        if (isWeb()) {
          await confirmationResult.confirm(otp);
        } else {
          await FirebaseAuthentication.confirmVerificationCode({
            verificationId: confirmationResult,
            verificationCode: otp,
          });
        }
        dispatch(markPinNotExist());
        navigate("/pin/set");
        analytics.track("OTP Verification reset pin Success", { phone });
      }
    } catch (error) {
      if (verificationMethod === "email") {
        setError("Invalid OTP. Please try again.");
        setLoader(false);
        setCooldown(false);
      } else {
        handlePhoneNumberError(error, setError, setLoader, setCooldown, phone);
      }
    } finally {
      setLoader(false);
    }
  };

  return (
    <Container>
      <Box pt={4} pb={4}>
        <Heading title="ENTER OTP" sx={{ mt: 2 }} />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Button
          onClick={() => setOtpSent(false)}
          startIcon={<ArrowBackIcon />}
          sx={{ textTransform: "none", color: "text.secondary" }}
        >
          Back to Enter Pin
        </Button>
      </Box>

      <Typography variant="body2" textAlign="center" sx={{ mt: 1 }}>
        {verificationMethod === "email"
          ? email &&
            !isAnonymous &&
            `Enter the email OTP ${!email.includes(
              "@privaterelay.appleid.com"
            ) ? email : "Apple email"}`
          : phone && !isAnonymous && `Enter the phone OTP sent to ${phone}`}
      </Typography>

      <Box paddingY={2}>
        <OTPInput
          value={otp}
          label="OTP"
          inputType="number"
          shouldAutoFocus="true"
          onChange={setOtp}
          numInputs={6}
          renderSeparator={<span style={{ margin: 5 }}></span>}
          renderInput={(props) => (
            <input
              {...props}
              style={{
                width: "100%",
                borderRadius: "8px",
                border: "1px solid #E0E0E0",
                height: "40px",
                fontSize: 18,
                textAlign: "center",
                fontFamily: "Arial",
              }}
            />
          )}
        />
        {error && (
          <Typography textAlign={"center"} sx={{ color: "red" }}>
            {error}
          </Typography>
        )}
        <Box paddingY={2}>
          <Button
            variant="contained"
            color="brand"
            onClick={handleVerifyOtp}
            fullWidth
            margin="normal"
            size="large"
          >
            Verify OTP
          </Button>
        </Box>
        
        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Typography color="text.secondary" variant="body2">
            {cooldown
              ? `Resend code in ${cooldownTime}s`
              : "Didn't receive the code?"}
          </Typography>
          <Button
            onClick={handleResendOtp}
            disabled={isResendDisabled}
            sx={{
              mt: 1,
              textTransform: "none",
              fontSize: "0.9rem",
            }}
          >
            Send again
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default ResetPin;
